/**
 * Reuniões do lead.
 *
 * A reunião é o único registro do produto cuja realização conta como interação
 * real (§7.7): conversar com o lead é contato, agendar não é. Por isso o
 * domínio separa três instantes que o protótipo tratava como um só:
 *
 * - `startsAt` — quando está marcada, no futuro ou no passado;
 * - `heldAt` — quando aconteceu de fato, confirmado por quem participou;
 * - `cancelledAt` — quando não vai mais acontecer.
 *
 * Só `heldAt` mexe em `last_interaction_at`, e só por um comando explícito.
 * Marcar uma reunião no futuro e nunca confirmá-la não pode fazer o lead
 * parecer aquecido: seria exatamente o erro que §7.7 existe para impedir.
 */

export interface Meeting {
  id: string;
  leadId: string;
  /** Participação em que a reunião acontece, quando conhecida. */
  membershipId?: string;
  /** Objetivo da conversa; é o título exibido na agenda e na jornada. */
  title: string;
  /** Início marcado, em UTC. */
  startsAt: string;
  durationMinutes: number;
  /** Nomes ou endereços livres; a agenda do produto não é lista fechada. */
  participants: string[];
  notes?: string;
  /** Link da sala; somente `https:` ou `http:` (§26.3). */
  link?: string;
  /** Confirmação de que aconteceu; é o que conta como interação. */
  heldAt?: string;
  cancelledAt?: string;
  cancelReason?: string;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export type MeetingStatus = "scheduled" | "late" | "held" | "cancelled";

export const MEETING_STATUS_LABEL: Record<MeetingStatus, string> = {
  scheduled: "Agendada",
  late: "Sem confirmação",
  held: "Realizada",
  cancelled: "Cancelada",
};

export const MIN_MEETING_MINUTES = 5;
export const MAX_MEETING_MINUTES = 480;

/**
 * Situação da reunião em um instante.
 *
 * `late` é uma reunião cuja hora passou sem confirmação — não é "realizada"
 * nem "cancelada", e apresentar como qualquer uma das duas seria inventar um
 * fato que ninguém registrou.
 */
export function meetingStatus(meeting: Meeting, now: string): MeetingStatus {
  if (meeting.cancelledAt) return "cancelled";
  if (meeting.heldAt) return "held";
  const end = Date.parse(meeting.startsAt) + meeting.durationMinutes * 60_000;
  const moment = Date.parse(now);
  if (!Number.isNaN(end) && !Number.isNaN(moment) && moment > end) return "late";
  return "scheduled";
}

/** Reuniões abertas de um lead, da mais próxima para a mais distante. */
export function upcomingMeetings(
  meetings: readonly Meeting[],
  leadId: string,
  now: string,
): Meeting[] {
  return meetings
    .filter(
      (meeting) =>
        meeting.leadId === leadId &&
        !meeting.cancelledAt &&
        !meeting.heldAt &&
        meeting.startsAt >= now,
    )
    .sort((left, right) => left.startsAt.localeCompare(right.startsAt));
}

const ALLOWED_PROTOCOLS = new Set(["https:", "http:"]);

/** Aceita apenas link navegável; `javascript:` e afins são recusados. */
export function isAllowedMeetingLink(value: string): boolean {
  try {
    return ALLOWED_PROTOCOLS.has(new URL(value).protocol);
  } catch {
    return false;
  }
}
